import { Clock, Mail, MapPin, Phone } from "lucide-react";
import { ADDRESS_ONE_LINE, BUSINESS, MAPS_URL } from "@/lib/site";
import { trackEvent } from "@/lib/analytics";
import { Reveal } from "../Reveal";

const CARDS = [
  {
    icon: Phone,
    label: "Call Us",
    body: BUSINESS.phone,
    href: BUSINESS.phoneHref,
    onClick: () => trackEvent("phone_link_clicked", { location: "contact_info" }),
  },
  {
    icon: Mail,
    label: "Email Us",
    body: BUSINESS.email,
    href: `mailto:${BUSINESS.email}`,
    onClick: () => trackEvent("email_link_clicked", { location: "contact_info" }),
  },
  {
    icon: MapPin,
    label: "Visit Us",
    body: `${BUSINESS.street}\n${BUSINESS.city}, ${BUSINESS.region} ${BUSINESS.postalCode}`,
    href: MAPS_URL,
    external: true,
    onClick: () => trackEvent("directions_clicked", { location: "contact_info" }),
  },
  {
    icon: Clock,
    label: "Office Hours",
    body: BUSINESS.officeHours,
  },
] as const;

export function ContactInfo() {
  return (
    <div>
      <Reveal x={-28}>
        <p className="eyebrow text-clay">Get in Touch</p>
        <h2
          id="contact-info-title"
          className="mt-4 font-display text-3xl leading-tight sm:text-4xl lg:text-[2.7rem]"
        >
          Let&apos;s Plan Your Stay
        </h2>
        <p className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
          Whether you&apos;re planning a fishing trip, traveling for work, organizing a family
          gathering, or looking for a comfortable place to stay near Falcon Lake, our team is ready
          to help.
        </p>
      </Reveal>

      <ul className="mt-10 grid gap-4 sm:grid-cols-2">
        {CARDS.map((card, i) => (
          <Reveal as="li" key={card.label} delay={i * 0.06} y={20}>
            {"href" in card && card.href ? (
              <a
                href={card.href}
                {...("external" in card && card.external
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
                onClick={"onClick" in card ? card.onClick : undefined}
                className="group flex min-h-[120px] h-full flex-col rounded-sm border border-sand/70 bg-surface p-5 transition-colors hover:border-forest/30"
                aria-label={
                  card.label === "Visit Us"
                    ? `Get directions to ${BUSINESS.name} at ${ADDRESS_ONE_LINE}`
                    : undefined
                }
              >
                <card.icon
                  className="size-5 text-forest transition-colors group-hover:text-clay"
                  aria-hidden="true"
                />
                <p className="eyebrow mt-4 text-muted-foreground">{card.label}</p>
                <p className="mt-2 whitespace-pre-line font-display text-xl text-forest-deep">
                  {card.body}
                </p>
              </a>
            ) : (
              <div className="flex min-h-[120px] h-full flex-col rounded-sm border border-sand/70 bg-surface p-5">
                <card.icon className="size-5 text-forest" aria-hidden="true" />
                <p className="eyebrow mt-4 text-muted-foreground">{card.label}</p>
                <p className="mt-2 whitespace-pre-line font-display text-xl text-forest-deep">
                  {card.body}
                </p>
              </div>
            )}
          </Reveal>
        ))}
      </ul>
    </div>
  );
}
